import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HQ } from "../sportConfig";
import "./AppreciationSummary.css";

const number = value => new Intl.NumberFormat("sv-SE").format(value);

// Public fixture totals only. Goal totals include both sides; no player/team credit is inferred.
export default function AppreciationSummary({ games, active = true, inline = false }) {
  const [open, setOpen] = useState(false), [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false), [version, setVersion] = useState(0);
  const [data, setData] = useState(null), [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    const preference = () => {
      if (alive) setVisible((window.SportsHQSettings || window.localStorage).getItem('sports-hq-show-likes') !== '0');
    };
    Promise.resolve(window.SPORTS_HQ_SETTINGS_READY).then(() => { if (alive) { preference(); setReady(true); } });
    const changed = () => setVersion(value => value + 1);
    window.addEventListener('sports-hq-settings-state', preference);
    window.addEventListener('storage', preference);
    window.addEventListener('sports-hq-social-change', changed);
    return () => {
      alive = false;
      window.removeEventListener('sports-hq-settings-state', preference);
      window.removeEventListener('storage', preference);
      window.removeEventListener('sports-hq-social-change', changed);
    };
  }, []);
  const fixtures = new Map();
  for (const game of games || []) {
    const key = window.SportsHQFollows?.keyFromLink(game.match_link, HQ.sport);
    if (key) fixtures.set(key, game);
  }
  const keys = [...fixtures.keys()].sort(), signature = JSON.stringify(keys);
  useEffect(() => {
    setData(null); setError(false);
    if (!ready || !visible || !active || (!inline && !open) || !keys.length) return;
    const controller = new AbortController();
    const load = async () => {
      const items = [];
      // Season lists can exceed the existing60-key API boundary; batch rather than request per fixture.
      for (let offset = 0; offset < keys.length; offset += 60) {
        const query = new URLSearchParams(keys.slice(offset, offset + 60).map(key => ['keys', key]));
        const response = await fetch(`/sportsim/api/social/appreciation?${query}`, { cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw Error('appreciation');
        const result = await response.json();
        if (!Array.isArray(result.items)) throw Error('appreciation');
        items.push(...result.items);
      }
      if (!controller.signal.aborted) setData(items);
    };
    load().catch(failure => { if (failure.name !== 'AbortError') setError(true); });
    return () => controller.abort();
    // signature represents canonical fixture identities, independent of object/array recreation.
  }, [signature, ready, visible, active, inline, open, version]);
  if (!ready || !visible || !keys.length) return null;
  const matchLikes = data?.reduce((sum, item) => sum + (item.match_likes || 0), 0) || 0;
  const goalLikes = data?.reduce((sum, item) => sum + (item.goal_likes || 0), 0) || 0;
  const liked = data?.filter(item => item.match_likes || item.goal_likes) || [];
  const body = <div className="appreciation__body">
    {error ? <p role="status">Gillningarna kunde inte hämtas. <button type="button" onClick={() => setVersion(value => value + 1)}>Försök igen</button></p>
      : !data ? <p role="status">Hämtar gillningar…</p>
        : !liked.length ? <p>Inga gillningar att visa ännu.</p>
          : <>
            <dl><div><dt>{inline ? 'Matchen' : 'Matcherna'}</dt><dd>{matchLikes ? number(matchLikes) : '–'}</dd></div>
              <div><dt>{inline ? 'Målen' : 'Mål i matcherna'}</dt><dd>{goalLikes ? number(goalLikes) : '–'}</dd></div></dl>
            {!inline && <ul>{liked.slice().sort((a, b) => String(fixtures.get(b.key)?.starts_at || '').localeCompare(String(fixtures.get(a.key)?.starts_at || ''))).slice(0, 3).map(item => {
              const game = fixtures.get(item.key);
              return <li key={item.key}><Link to={`/match?url=${encodeURIComponent(game.match_link)}`}>
                {game.home_team} – {game.away_team}<span aria-hidden="true"> ↗</span></Link></li>;
            })}</ul>}
          </>}
    {!inline && <p className="appreciation__note">Båda lagens mål ingår.</p>}
  </div>;
  return inline ? <section className="appreciation appreciation--inline" aria-label="Uppskattning"><h3>Uppskattning</h3>{body}</section>
    : <details className="appreciation" open={open} onToggle={event => setOpen(event.currentTarget.open)}><summary>Uppskattning</summary>{open && body}</details>;
}
